import React, { useRef, useState } from "react";
import { Alert, Button, ConfigProvider, InputNumber, Modal } from "antd";
import zhCN from "antd/locale/zh_CN";
import { formatConfigurations, formatPrice } from "./matrix.js";
import { validateProductPrice } from "./price.js";
import "./product-details.css";
import BrandLabel from "./BrandLabel.jsx";

export function EditIcon() {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            aria-hidden="true"
        >
            <path d="m15 5 4 4M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15z" />
        </svg>
    );
}

export default function ProductDetails({
                                           product,
                                           brand,
                                           onClose,
                                           onSavePrice,
}) {
    const [editing, setEditing] = useState(false);
    function openEditor() {
        setEditing(true);
    }
    return (
        <ConfigProvider
            locale={zhCN}
            theme={{ token: { colorPrimary: "#6157b9", borderRadius: 8 } }}
        >
            <Modal
                open
                title="产品详情"
                className="product-details-modal"
                width={520}
                keyboard={!editing}
                maskClosable={!editing}
                onCancel={() => {
                    if (!editing) onClose();
                }}
                closeIcon={<span aria-label="关闭详情">×</span>}
                footer={null}
            >
                {/*
                <div
                    className="detail-brand"
                    style={{ color: getBrandLogoColor(brand) }}
                >
                    {brand.name}
                </div>
                */}
                <h2 className="detail-product-name">
                    {product.name}
                    <BrandLabel brand={brand} />
                </h2>
                <div className="detail-price-row">
                    <div className="detail-price">
                        ${formatPrice(product.price)} <span>USD</span>
                    </div>
                    {/*
                    <Button
                        type="text"
                        className="edit-price-button"
                        aria-label="编辑商品价格"
                        title="编辑价格"
                        icon={<EditIcon />}
                        onClick={openEditor}
                    />
                    */}
                </div>
                <dl className="detail-specs">
                    <div className="detail-configurations">
                        <dt>RAM / ROM（内存 / 存储）</dt>
                        <dd>{formatConfigurations(product) || "未指定"}</dd>
                    </div>
                    <div>
                        <dt>上市日期</dt>
                        <dd>{product.launchDate_str || "未指定"}</dd>
                    </div>
                    <div>
                        <dt>适用市场</dt>
                        <dd>{product.market || "—"}</dd>
                    </div>
                    {/*
                     <div>
                        <dt>国家</dt>
                        <dd>{product.country || "未指定"}</dd>
                    </div>
                    */}
                    <div>
                        <dt>尺寸、分辨率</dt>
                        <dd>
                            {product.display?.size}<br/>{product.display?.resolution}
                        </dd>
                    </div>
                    <div>
                        <dt>电池</dt>
                        <dd>
                            {product.battery?.capacity_mah} mAh<br/>
                            {product.battery?.wired_charging_power_w ? `${product.battery?.wired_charging_power_w}W (wired)` : ''}<br/>
                            {product.battery?.reverse_wired_charging_power_w ? `${product.battery?.reverse_wired_charging_power_w}W (Reverse wired)` : ''}
                        </dd>
                    </div>
                    {
                        product.camera && Array.isArray(product.camera) && product.camera.length > 0 && (
                            <div>
                                <dt>摄像</dt>
                                <dd>
                                    {product.camera?.map(item=>`${item.mp}(${item.lens_type})`).join(" + ")}
                                </dd>
                            </div>
                        )
                    }
                    <div>
                        <dt>网络制式</dt>
                        <dd>{product.technology}</dd>
                    </div>
                    <div>
                        <dt>NFC</dt>
                        <dd>{product.nfc || "—"}</dd>
                    </div>
                    <div>
                        <dt>防水</dt>
                        <dd>{product.ip_rating || "—"}</dd>
                    </div>
                    <div>
                        <dt>指纹</dt>
                        <dd>{product.has_fingerprint || "—"}</dd>
                    </div>
                </dl>
                {/*
                <p className="detail-description">
                    {product.description || "暂无商品描述"}
                </p>
                */}
                <div className="detail-gsm-link">
                    <Button type={"link"} href={product.gsmLink} target={"_blank"}>
                        查看详情产品
                    </Button>
                </div>
            </Modal>
            {editing && (
                <PriceEditor
                    product={product}
                    brand={brand}
                    onClose={() => setEditing(false)}
                    onSavePrice={onSavePrice}
                />
            )}
        </ConfigProvider>
    );
}

export function PriceEditor({ product, brand, onClose, onSavePrice }) {
    const [draftPrice, setDraftPrice] = useState(String(product.price));
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const priceInput = useRef(null);
    async function savePrice() {
        if (saving) return;
        const nextError = validateProductPrice(draftPrice);
        if (nextError) {
            setError(nextError);
            return;
        }
        setSaving(true);
        try {
            await onSavePrice(product.id, Number(draftPrice));
            onClose();
        } catch (reason) {
            setError(reason.message || "价格保存失败，请重试。");
        } finally {
            setSaving(false);
        }
    }
    return (
        <ConfigProvider
            locale={zhCN}
            theme={{ token: { colorPrimary: "#6157b9", borderRadius: 8 } }}
        >
            <Modal
                open
                title="编辑商品价格"
                className="price-edit-modal"
                width={400}
                onCancel={() => {
                    if (!saving) onClose();
                }}
                keyboard={!saving}
                maskClosable={!saving}
                closable={!saving}
                footer={null}
                afterOpenChange={(open) => {
                    if (open) priceInput.current?.focus();
                }}
            >
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        savePrice();
                    }}
                    noValidate
                >
                    <p className="edit-product-name">
                        {brand.name} · {product.name}
                    </p>
                    <label htmlFor="edit-product-price">商品价格（USD）</label>
                    <InputNumber
                        ref={priceInput}
                        id="edit-product-price"
                        aria-label="商品价格（USD）"
                        disabled={saving}
                        stringMode
                        value={draftPrice || null}
                        step="0.01"
                        prefix="$"
                        status={error ? "error" : undefined}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? "price-edit-error" : "price-edit-hint"}
                        onChange={(value) => {
                            setDraftPrice(value == null ? "" : String(value));
                            setError("");
                        }}
                    />
                    {error && (
                        <div id="price-edit-error" role="alert">
                            <Alert type="error" showIcon title={error} />
                        </div>
                    )}
                    <p id="price-edit-hint" className="price-edit-hint">
                        支持非负价格，最多两位小数。保存调用价格更新接口后重新请求商品；若不满足当前筛选或价格范围，卡片将从当前视图移除。演示服务仅在内存保存，真实保存需接入后端。
                    </p>
                    <div className="price-edit-actions">
                        <Button aria-label="取消" onClick={onClose} disabled={saving}>
                            取消
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            aria-label="保存价格"
                            loading={saving}
                        >
                            保存价格
                        </Button>
                    </div>
                </form>
            </Modal>
        </ConfigProvider>
    );
}
